import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import React, { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Side } from "@/components/ui/customChervonIcon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type TProps = {
  onChange: (val?: Date) => void;
  validateDate?: (date: Date) => boolean;
} & {
  defaultValue?: Date;
  dateLimit?: Date;
  className?: string;
  placeholder?: string;
  isDateTime?: boolean;
  showTimePicker?: boolean;
  side?: Side;
  disabled?: boolean;
};

const DateInput: React.FC<TProps> = ({
  onChange,
  defaultValue,
  dateLimit,
  validateDate,
  className,
  placeholder,
  side,
  disabled,
}) => {
  const [date, setDate] = useState<Date | undefined>(defaultValue);

  const handleChange = React.useCallback(
    (val?: Date) => {
      setDate(val);
      onChange(val);
    },
    [onChange],
  );

  useEffect(() => {
    if (defaultValue && typeof defaultValue === "string") {
      setDate(new Date(defaultValue));
    } else {
      // if reset or default value is undefined then it should be set so
      setDate(defaultValue);
    }
  }, [defaultValue]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant={"outline"}
          className={cn(className, "w-[90%] pl-3 text-left font-normal ")}
          disabled={disabled}
        >
          {date ? (
            format(date, "PPP")
          ) : (
            <span className=' opacity-30'>{placeholder ?? "Pick a date"}</span>
          )}
          <CalendarIcon className='ml-auto opacity-50 min-w-4 min-h-4 max-w-4 max-h-4' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='p-1' side={side}>
        <Calendar
          mode='single'
          selected={date as Date}
          onSelect={(val) => handleChange(val)}
          disabled={(date: Date) => {
            if (validateDate) {
              return validateDate(date);
            }

            let limitDate: Date;

            if (typeof dateLimit === "string") {
              const parsed = new Date(dateLimit);
              limitDate = isNaN(parsed.getTime())
                ? new Date("1900-01-01")
                : parsed;
            } else {
              limitDate = dateLimit ?? new Date("1900-01-01");
            }

            return date < limitDate;
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DateInput;
